import moment from 'moment';
import { useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip,
  YAxis,
  ResponsiveContainer
} from 'recharts';
import { cToF } from '../../utils';

export type TemperatureChartProps = {
  style?: React.CSSProperties;
  records?: {
    data: number;
    targetData?: number
    time: string;
  }[];
  unit?: 'C' | 'F' | 'RH' | undefined;
  dateFormat?: string;
  XAxisTickFormatterTimeFormat?: string;
  slidePercent?: number;        // 用于滑动窗口百分比
  durationHours?: number;       // 新增，展示的窗口长度（小时），为空显示全部
  onStartAdnEndTime?(data: {
    startStr: string
    endStr: string
  }): void
};


type DataItem = {
  timeFormat: string;
  data: number;
  time: string;
  name: number;
};

function TemperatureChart(props: TemperatureChartProps) {
  const unit =
    props.unit === 'F'
      ? '℉'
      : props.unit === 'C'
        ? '℃'
        : '%';

  const yDomain =
    props.unit === 'F'
      ? [32, 140]
      : props.unit === 'C'
        ? [0, 60]
        : [0, 100];

  const dateFormat = props.dateFormat || 'DD.MM.YYYY';
  const XAxisTickFormatterTimeFormat = props.XAxisTickFormatterTimeFormat || 'HH:mm';

  // 排序后的数据
  const sortedRecords = props.records || []


  const firstTime = sortedRecords.length ? moment(sortedRecords[0].time) : moment();
  const lastTime = sortedRecords.length ? moment(sortedRecords[sortedRecords.length - 1].time) : moment();
  const totalDuration = lastTime.diff(firstTime);

  // 判断窗口相关逻辑
  let windowStart: moment.Moment;
  let windowEnd: moment.Moment;
  let useSlidingWindow = Boolean(props.durationHours); // 如果 durationHours 有值，才做滑动，否则全量

  if (useSlidingWindow) {
    // durationHours 有值时，根据slidePercent和时长窗口滑动
    const windowMs = props.durationHours! * 3600 * 1000;
    if (totalDuration <= windowMs) {
      // 不足一个窗口，直接取全部数据
      windowStart = firstTime.clone();
      windowEnd = lastTime.clone();
    } else {
      // 滑动窗口
      const slidePercent = props.slidePercent ?? 1;
      // 可移动区间的最大开始时间 = lastTime - window长度
      const maxWindowStart = lastTime.clone().subtract(props.durationHours!, 'hours');
      const minWindowStart = firstTime.clone();
      // slidePercent=0 显示最早，1时显示最后
      const slideDuration = maxWindowStart.diff(minWindowStart); // 可滑动的总毫秒数
      const slideMs = Math.floor(slideDuration * slidePercent);
      windowStart = minWindowStart.clone().add(slideMs, 'ms');
      windowEnd = windowStart.clone().add(props.durationHours!, 'hours');
      if (windowEnd.isAfter(lastTime)) windowEnd = lastTime.clone();
    }
  } else {
    // durationHours 为空，显示全部
    windowStart = firstTime.clone();
    windowEnd = lastTime.clone();
  }


  function findWindowIndices(arr: string | any[], windowStart: moment.MomentInput, windowEnd: moment.MomentInput) {
    // arr 已经按时间升序, windowStart/End 是 moment对象
    const N = arr.length;
    let left = 0, right = N - 1;
    let startIdx = N, endIdx = N;

    // 找第一个 >= windowStart
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const t = moment(arr[mid].time);
      if (t.isSameOrAfter(windowStart)) {
        startIdx = mid;
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    // 再找第一个 >= windowEnd
    left = startIdx; right = N - 1;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const t = moment(arr[mid].time);
      if (t.isSameOrAfter(windowEnd)) {
        endIdx = mid;
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }
    return [startIdx, endIdx];
  }



  const templateData: DataItem[] = useMemo(() => {

    if (sortedRecords.length === 0) return [];

    let visibleArr: typeof sortedRecords;

    if (useSlidingWindow) {
      // 用二分法定位索引
      const [startIdx, endIdx] = findWindowIndices(sortedRecords, windowStart, windowEnd);
      // slice窗口区间数据，注意 endIdx 可能超出
      visibleArr = sortedRecords.slice(startIdx, endIdx);

    } else {
      visibleArr = sortedRecords;
    }

    // 后面逻辑和原来一样
    const arr: DataItem[] = visibleArr.map((item, idx) => ({
      ...item,
      timeFormat: moment(item.time).format(XAxisTickFormatterTimeFormat),
      name: idx,
    }));

    // 只有一个点时补多一个，保证折线可见
    if (arr.length === 1) {
      const singlePoint = arr[0];
      const endPoint = {
        ...singlePoint,
        time: moment(singlePoint.time).add(5, 'minute').toISOString(),
        timeFormat: moment(singlePoint.time).add(5, 'minute').format(XAxisTickFormatterTimeFormat),
        name: 1,
      };
      return [singlePoint, endPoint];
    }
    return arr;
  }, [sortedRecords, useSlidingWindow, windowStart, windowEnd, XAxisTickFormatterTimeFormat]);



  useEffect(() => {
    if (!props.onStartAdnEndTime) return
    let startStr = ''
    let endStr = ''

    if (useSlidingWindow) {
      startStr = windowStart.format(dateFormat + ' HH:mm')
      endStr = windowEnd.format(dateFormat + ' HH:mm')
    } else {
      startStr = firstTime.format(dateFormat + ' HH:mm')
      endStr = lastTime.format(dateFormat + ' HH:mm')
    }

    props.onStartAdnEndTime({
      startStr,
      endStr
    })

  }, [props.slidePercent, props.durationHours, sortedRecords])

  return (
    <div
      className="temperature-chart flex"
      style={{ position: 'relative', width: '55vw', ...(props.style || {}) }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={templateData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            interval={'preserveStartEnd'}
            strokeWidth={3}
            stroke="white"
            dataKey="name"
            tickFormatter={item => `    ${templateData[item]?.timeFormat || ''}   `}
            domain={['dataMin', 'dataMax']}
            type="number"
          />
          <YAxis
            strokeWidth={3}
            stroke="white"
            dataKey="data"
            tickFormatter={item => `${item}${unit}`}
            domain={yDomain}
            ticks={yDomain}
            type="number"
          />

          <Tooltip content={<CustomTooltip unit={unit} dateFormat={dateFormat} />} />
          <Line dot={false} animationDuration={300} type="monotone" dataKey="targetData" stroke="#406F70" strokeWidth={1.5} isAnimationActive={false} />
          <Line dot={false} animationDuration={300} type="monotone" dataKey="data" stroke="white" strokeWidth={1.5} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      <div style={{
        position: 'absolute',
        right: -45,
        bottom: 10,
        color: 'white',
        fontWeight: 700,
        fontSize: 20,
      }}>Time</div>
      <div style={{
        position: 'absolute',
        left: -20,
        top: -20,
        color: 'white',
        fontWeight: 700,
        fontSize: 20,
        width: 100,
        textAlign: 'right',
      }}>{unit === '%' ? 'RH%' : unit}</div>

    </div>
  );
}

export default TemperatureChart;

// Tooltip 组件保持不变
const CustomTooltip = (props: any) => {
  if (props.active && props.payload && props.payload.length) {
    const { payload } = props;
    const dateFormat = props.dateFormat;

    const targetData = payload[0]?.payload?.targetData
    const data = payload[0]?.payload?.data
    const time = payload[0]?.payload?.time

    return (
      <div className="custom-tooltip" style={{
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        padding: "10px"
      }}>
        <p className="label">{`Time : ${moment(time).format(`${dateFormat} HH:mm`)}`}</p>
        <p className="intro">
          {`${props.unit === '%' ? 'Humidity' : 'Temperature'} : ${data}${props.unit}`}
        </p>

        {targetData !== undefined && (
          <p className="intro"
            style={{
              color: '#406F70'
            }}
          >
            {`${props.unit === '%' ? 'TargetHumidity' : 'TargetTemperature'} : ${targetData}${props.unit}`}
          </p>
        )}



      </div>
    );
  }
  return null;
};
